import React, { useRef, useState } from 'react';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, CircularProgress, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { InvoicesRoutes } from '../../../Router/path';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { formatBookingCurrency } from '../Helpers/formatBookingCurrency';
import { useBookingReservationInvoicesLookupQuery } from '../Service/bookingReservationInvoicesLookupApi';
import type { BookingReservationInvoiceLookup } from '../Types/bookingReservationTable.types';
import {
  useLazyGetInvoicePdfQuery,
  useRegenerateInvoicePdfMutation,
} from '../../Invoices/Service/extendedInvoicesApi';
import { downloadInvoicePdf } from '../../Invoices/utils/invoicePdfUtils';

interface BookingReservationInvoiceLinksCellProps {
  reservationId: number;
}

const BookingReservationInvoiceLinksCell: React.FC<BookingReservationInvoiceLinksCellProps> = ({
  reservationId,
}) => {
  const { invoices, isLoading, isError, errorMessage } =
    useBookingReservationInvoicesLookupQuery(reservationId);
  const [triggerGetInvoicePdf] = useLazyGetInvoicePdfQuery();
  const [regenerateInvoicePdf] = useRegenerateInvoicePdfMutation();
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<number | null>(null);
  const [refreshingIds, setRefreshingIds] = useState<ReadonlySet<number>>(new Set());
  const refreshInFlightRef = useRef<Set<number>>(new Set());

  const handleDownloadPdf = async (invoice: BookingReservationInvoiceLookup) => {
    setDownloadingInvoiceId(invoice.id);

    try {
      await downloadInvoicePdf(
        triggerGetInvoicePdf,
        invoice.id,
        `Faktura_${invoice.number.replace(/[\\/:*?"<>|]/g, '_')}.pdf`
      );
    } catch (error) {
      console.error('Invoice PDF download failed:', error);
      window.alert(extractApiErrorMessage(error, 'Nie udało się pobrać PDF faktury.'));
    } finally {
      setDownloadingInvoiceId((currentId) => (currentId === invoice.id ? null : currentId));
    }
  };

  const handleRefreshPdf = async (invoice: BookingReservationInvoiceLookup) => {
    // disabled only applies after re-render, so a ref guards a fast second click
    if (refreshInFlightRef.current.has(invoice.id)) {
      return;
    }
    refreshInFlightRef.current.add(invoice.id);
    setRefreshingIds((current) => new Set(current).add(invoice.id));

    try {
      await regenerateInvoicePdf({ id: invoice.id }).unwrap();
    } catch (error) {
      console.error('Invoice PDF refresh failed:', error);
      window.alert(extractApiErrorMessage(error, 'Nie udało się odświeżyć PDF faktury.'));
    } finally {
      refreshInFlightRef.current.delete(invoice.id);
      setRefreshingIds((current) => {
        const next = new Set(current);
        next.delete(invoice.id);
        return next;
      });
    }
  };

  if (isLoading && invoices.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        Ładowanie...
      </Typography>
    );
  }

  if (isError) {
    return (
      <Typography variant="caption" color="error">
        {errorMessage ?? 'Nie udało się pobrać powiązanych faktur.'}
      </Typography>
    );
  }

  if (invoices.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
      {invoices.map((invoice) => (
        <Box key={invoice.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
            <Tooltip title={invoice.typeLabel} placement="top-start">
              <Link
                component={RouterLink}
                to={InvoicesRoutes.update.replace(':invoiceId', String(invoice.id))}
                underline="hover"
                variant="body2"
                sx={{ lineHeight: 1.35, wordBreak: 'break-word' }}
              >
                {invoice.number}
              </Link>
            </Tooltip>
            <Tooltip title="Pobierz PDF" placement="top">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={`Pobierz PDF ${invoice.number}`}
                  onClick={() => void handleDownloadPdf(invoice)}
                  disabled={downloadingInvoiceId === invoice.id || refreshingIds.has(invoice.id)}
                  sx={{ p: 0.25 }}
                >
                  {downloadingInvoiceId === invoice.id ? (
                    <CircularProgress size={14} />
                  ) : (
                    <PictureAsPdfIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Odśwież PDF" placement="top">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={`Odśwież PDF ${invoice.number}`}
                  onClick={() => void handleRefreshPdf(invoice)}
                  disabled={refreshingIds.has(invoice.id) || downloadingInvoiceId === invoice.id}
                  sx={{ p: 0.25 }}
                >
                  {refreshingIds.has(invoice.id) ? (
                    <CircularProgress size={14} />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {invoice.totalGross !== null
              ? formatBookingCurrency(invoice.totalGross, invoice.currency)
              : '-'}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default BookingReservationInvoiceLinksCell;
